// ABOUTME: Defines TanStack Query hooks for admin dashboard data and mutations.
import {
  operatorsApi,
  pollingApi,
  configsApi,
  historyApi,
  notificationSettingsApi,
  syncRecordsApi,
  PollingItem,
  Config,
  ConfigPatchFields,
  PollingPatchFields,
  CreateNotificationRecipientInput,
  UpdateNotificationRecipientInput,
} from '../api/adminApi';
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';

/**
 * Intelligent Caching Strategy
 *
 * 1. OPERATORS (Static Data):
 *    - staleTime: Infinity - never refetch automatically
 *    - gcTime: Infinity - keep in cache forever
 *    - Rationale: Operator names/IDs never change
 *
 * 2. POLLING & CONFIGS (Dynamic Data):
 *    - staleTime: 2 minutes - mark data as stale after 2 minutes
 *    - gcTime: 1 hour - keep in cache for 1 hour
 *    - refetchOnWindowFocus: true - check for new items when user returns
 *    - Rationale: Documents can change, new ones can be added
 *    - Benefit: Shows cached data instantly, refetches in background if stale
 *
 * 3. CACHE INVALIDATION:
 *    - Mutations automatically invalidate related queries
 *    - When user edits/creates, only affected queries refresh
 *    - Manual refresh available via queryClient.invalidateQueries()
 *
 * This approach ensures:
 * - Instant page loads (cached data shown immediately)
 * - Fresh data (background refetch if > 2 minutes old)
 * - Detects new documents (refetch on window focus)
 * - No unnecessary network requests (respects cache times)
 */

// Query Keys
export const adminKeys = {
  all: ['admin'] as const,
  operators: () => [...adminKeys.all, 'operators'] as const,
  operator: (id: string) => [...adminKeys.operators(), id] as const,
  operatorAircraft: (id: string) =>
    [...adminKeys.operator(id), 'aircraft'] as const,
  // Own root key: integration status is dynamic and must never inherit the
  // static Infinity caching of the operators tree.
  operatorIntegrations: (id: string) =>
    [...adminKeys.all, 'operator-integrations', id] as const,
  notificationSettings: (id: string) =>
    [...adminKeys.all, 'notification-settings', id] as const,
  notificationHistory: (id: string) =>
    [...adminKeys.all, 'notification-history', id] as const,
  polling: (params?: Record<string, unknown>) =>
    [...adminKeys.all, 'polling', params] as const,
  pollingItem: (id: string) => [...adminKeys.all, 'polling', id] as const,
  pollingConfigs: (id: string) =>
    [...adminKeys.pollingItem(id), 'configs'] as const,
  configs: (params?: Record<string, unknown>) =>
    [...adminKeys.all, 'configs', params] as const,
  config: (id: string) => [...adminKeys.all, 'configs', id] as const,
  configSyncRecords: (id: string) =>
    [...adminKeys.config(id), 'sync-records'] as const,
  history: (collection: string, id: string) =>
    [...adminKeys.all, 'history', collection, id] as const,
  syncLogs: (params?: Record<string, unknown>) =>
    [...adminKeys.all, 'syncLogs', params] as const,
  syncLog: (id: string) => [...adminKeys.all, 'syncLogs', id] as const,
  syncedItems: (params?: Record<string, unknown>) =>
    [...adminKeys.all, 'syncedItems', params] as const,
};

// Operators Hooks
export function useOperators() {
  return useQuery({
    queryKey: adminKeys.operators(),
    queryFn: operatorsApi.getAll,
    staleTime: Infinity, // Operators never change - treat as static
    gcTime: Infinity, // Keep in cache forever
    refetchOnWindowFocus: false, // Don't refetch on tab focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });
}

export function useOperator(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.operator(id),
    queryFn: () => operatorsApi.getById(id),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOperatorAircraft(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.operatorAircraft(id),
    queryFn: () => operatorsApi.getAircraft(id),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useOperatorTechlogInfo(id: string, enabled = true) {
  return useQuery({
    queryKey: [...adminKeys.operator(id), 'techlog-info'] as const,
    queryFn: () => operatorsApi.getTechlogInfo(id),
    enabled,
    staleTime: Infinity, // Operator name never changes
    gcTime: Infinity,
  });
}

export function useOperatorIntegrations(operatorId: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.operatorIntegrations(operatorId),
    queryFn: () => operatorsApi.getIntegrations(operatorId),
    enabled: enabled && !!operatorId,
    staleTime: 2 * 60 * 1000, // 2 minutes - refetch in background if stale
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: true, // Toggles elsewhere change status
  });
}

// A whole-integration flip touches many polling and config docs at once —
// invalidate rather than patch caches document by document.
function invalidateIntegrationCaches(queryClient: QueryClient, id: string) {
  queryClient.invalidateQueries({
    queryKey: adminKeys.operatorIntegrations(id),
  });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'admin' &&
        (key[1] === 'polling' || key[1] === 'configs')
      );
    },
  });
}

export function useUpdateOperatorIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      integration,
      isDisabled,
    }: {
      id: string;
      integration: string;
      isDisabled: boolean;
    }) => operatorsApi.updateIntegration(id, integration, { isDisabled }),
    onSuccess: (_result, { id }) =>
      invalidateIntegrationCaches(queryClient, id),
  });
}

export function useUpdateOperatorIntegrations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isDisabled }: { id: string; isDisabled: boolean }) =>
      operatorsApi.updateAllIntegrations(id, { isDisabled }),
    onSuccess: (_result, { id }) =>
      invalidateIntegrationCaches(queryClient, id),
  });
}

// Notification Settings Hooks
export function useOperatorNotificationSettings(
  operatorId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: adminKeys.notificationSettings(operatorId),
    queryFn: () => notificationSettingsApi.list(operatorId),
    enabled: enabled && !!operatorId,
    staleTime: 60 * 1000,
  });
}

export function useNotificationHistory(operatorId: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.notificationHistory(operatorId),
    queryFn: () => notificationSettingsApi.history(operatorId),
    enabled: enabled && !!operatorId,
    staleTime: 30 * 1000,
  });
}

// A recipient write changes both the recipient list and the audit trail.
function invalidateNotificationCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  operatorId: string,
) {
  queryClient.invalidateQueries({
    queryKey: adminKeys.notificationSettings(operatorId),
  });
  queryClient.invalidateQueries({
    queryKey: adminKeys.notificationHistory(operatorId),
  });
}

export function useAddNotificationRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      operatorId,
      data,
    }: {
      operatorId: string;
      data: CreateNotificationRecipientInput;
    }) => notificationSettingsApi.create(operatorId, data),
    onSuccess: (_result, { operatorId }) =>
      invalidateNotificationCaches(queryClient, operatorId),
  });
}

export function useUpdateNotificationRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      operatorId,
      subId,
      data,
    }: {
      operatorId: string;
      subId: string;
      data: UpdateNotificationRecipientInput;
    }) => notificationSettingsApi.update(operatorId, subId, data),
    onSuccess: (_result, { operatorId }) =>
      invalidateNotificationCaches(queryClient, operatorId),
  });
}

export function useRemoveNotificationRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      operatorId,
      subId,
    }: {
      operatorId: string;
      subId: string;
    }) => notificationSettingsApi.remove(operatorId, subId),
    onSuccess: (_result, { operatorId }) =>
      invalidateNotificationCaches(queryClient, operatorId),
  });
}

// Polling Hooks
export function usePollingItems(operatorId?: string) {
  return useQuery({
    queryKey: adminKeys.polling(operatorId ? { operatorId } : undefined),
    queryFn: () => pollingApi.query(operatorId ? { operatorId } : {}),
    enabled: !!operatorId,
    staleTime: 2 * 60 * 1000, // 2 minutes - refetch in background if stale
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: true, // Check for new polling items when user returns
  });
}

export function usePollingItem(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.pollingItem(id),
    queryFn: () => pollingApi.getById(id),
    enabled,
  });
}

export function usePollingConfigs(pollingId: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.pollingConfigs(pollingId),
    queryFn: () => pollingApi.getConfigs(pollingId),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - refetch in background if stale
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: true, // Check for new configs when user returns
  });
}

export function useUpdatePolling() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PollingPatchFields }) =>
      pollingApi.update(id, data),
    onSuccess: (updatedPolling) => {
      // Update the specific polling item query
      queryClient.setQueryData(
        adminKeys.pollingItem(updatedPolling.id),
        updatedPolling,
      );

      // Update all polling list queries (with or without operatorId)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return (
              Array.isArray(key) && key[0] === 'admin' && key[1] === 'polling'
            );
          },
        },
        (
          old:
            | {
                polling?: PollingItem[];
                total?: number;
                page?: number;
                limit?: number;
              }
            | undefined,
        ) => {
          if (!old?.polling) return old;
          return {
            ...old,
            polling: old.polling.map((item: PollingItem) => {
              if (item.id === updatedPolling.id) {
                // Preserve list-only derived fields the PATCH response can't carry
                return {
                  ...updatedPolling,
                  configsCount: item.configsCount,
                  peerFlowsCount: item.peerFlowsCount,
                  destinations: item.destinations,
                };
              }
              return item;
            }),
          };
        },
      );
    },
  });
}

// Configs Hooks
export function useConfigs(params?: {
  origin?: string;
  destination?: string;
  entity?: string;
  originOperatorId?: string;
  destinationOperatorId?: string;
  isDisabled?: boolean;
}) {
  return useQuery({
    queryKey: adminKeys.configs(params),
    queryFn: () => configsApi.query(params || {}),
    staleTime: 2 * 60 * 1000, // 2 minutes - refetch in background if stale
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: true, // Check for new configs when user returns
  });
}

export function useConfig(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.config(id),
    queryFn: () => configsApi.getById(id),
    enabled,
  });
}

export function useConfigSyncRecords(configId: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.configSyncRecords(configId),
    queryFn: () => configsApi.getSyncRecords(configId),
    enabled,
  });
}

export function useUpdateConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConfigPatchFields }) =>
      configsApi.update(id, data),
    onSuccess: (updatedConfig) => {
      // Update the specific config query
      queryClient.setQueryData(
        adminKeys.config(updatedConfig.id),
        updatedConfig,
      );

      // Update all polling configs queries (find which polling item this config belongs to)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return (
              Array.isArray(key) &&
              key[0] === 'admin' &&
              key[1] === 'polling' &&
              key.length >= 4 &&
              key[3] === 'configs'
            );
          },
        },
        (old: { configs?: Config[] } | undefined) => {
          if (!old?.configs) return old;
          return {
            ...old,
            configs: old.configs.map((config: Config) =>
              config.id === updatedConfig.id ? updatedConfig : config,
            ),
          };
        },
      );

      // Update configs list queries
      queryClient.setQueriesData(
        {
          queryKey: adminKeys.configs(),
        },
        (
          old:
            | {
                configs?: Config[];
                total?: number;
                page?: number;
                limit?: number;
              }
            | undefined,
        ) => {
          if (!old?.configs) return old;
          return {
            ...old,
            configs: old.configs.map((config: Config) =>
              config.id === updatedConfig.id ? updatedConfig : config,
            ),
          };
        },
      );
    },
  });
}

export function useDocumentHistory(collection: string, id: string) {
  return useQuery({
    queryKey: adminKeys.history(collection, id),
    queryFn: () => historyApi.forDocument(collection, id),
    enabled: !!collection && !!id,
    staleTime: 0,
  });
}

export function useUpdateConfigSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, schema }: { id: string; schema: string }) =>
      configsApi.updateSchema(id, schema),
    onSuccess: (_result, { id }) => {
      // Two config key families are in play: the adminKeys tree and the raw
      // ['config', id] key ConfigSchemaPage queries with — refresh both.
      queryClient.invalidateQueries({ queryKey: adminKeys.config(id) });
      queryClient.invalidateQueries({ queryKey: ['config', id] });
    },
  });
}

// Sync Records Hooks
export function useSyncLogs(params?: {
  origin?: string;
  destination?: string;
  originOpId?: string;
  destinationOpId?: string;
  entity?: string;
  originEntityId?: string;
  destinationEntityId?: string;
  action?: 'CREATE' | 'UPDATE' | 'DELETE';
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.syncLogs(params),
    queryFn: () => syncRecordsApi.querySyncLogs(params || {}),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useSyncLog(id: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.syncLog(id),
    queryFn: () => syncRecordsApi.getSyncLogById(id),
    enabled,
  });
}

export function useSyncedItems(params?: {
  origin?: string;
  destination?: string;
  type?: string;
  originId?: string;
  destinationId?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.syncedItems(params),
    queryFn: () => syncRecordsApi.querySyncedItems(params || {}),
    staleTime: 1 * 60 * 1000,
  });
}

// Manual Refresh Utilities
export function useRefreshAdminData() {
  const queryClient = useQueryClient();

  return {
    refreshPolling: (operatorId?: string) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.polling(operatorId ? { operatorId } : undefined),
      });
    },
    refreshConfigs: (params?: Record<string, unknown>) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.configs(params),
      });
    },
    refreshAll: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.all,
      });
    },
  };
}
